import queryString from "query-string"
import { getSchoolYear } from "../../../mp-client/Utils"
import { CURRENT_PROGRAM_KEY } from "../../../mp-core/helpers/storage"
import { LicensesSet, User } from "../../../mp-database"
import { getProgramByLicenseName } from "../../../mp-core/helpers/getProgramByLicenseName"
import { Roles } from "../../generic-utils"
import {
  FeatureFlagName,
  FeatureFlagStatesType
} from "../../generic-utils/feature-flags/types"
import { getLicenseStatus } from "../hooks"

export default function getHomePath(
  user: User,
  licenses: LicensesSet[],
  program?: string,
  featureFlags?: FeatureFlagStatesType,
  hasOnlyACTLegacyLicenses?: boolean
) {
  let homePath = "/profile"

  // If hasOnlyACTLegacyLicenses is not provided, compute it
  if (hasOnlyACTLegacyLicenses === undefined) {
    const licenseStatus = getLicenseStatus(licenses)
    hasOnlyACTLegacyLicenses = licenseStatus.hasOnlyACTLegacyLicenses
  }

  if (
    !!featureFlags?.[FeatureFlagName.auto_redirect_to_reporting] &&
    !hasOnlyACTLegacyLicenses &&
    ![
      Roles.Student,
      Roles.Instructor,
      Roles.Sales_Rep,
      Roles.Course_Author
    ].includes(user?.role)
  ) {
    return "/reporting"
  }

  if (
    user?.role === Roles.Student &&
    !hasOnlyACTLegacyLicenses &&
    !!featureFlags?.[FeatureFlagName.auto_redirect_to_student_experience]
  ) {
    return "/student-experience"
  }

  // Handle ACT Admin and Organization Admin first
  if (
    user?.role === Roles.ACT_Admin ||
    user?.role === Roles.ACT_Multi_District_Admin
  ) {
    return "/reporting/organization/states"
  }

  if (
    user &&
    user.role !== Roles.Super_Administrator &&
    user.role !== Roles.Sales_Rep &&
    user.role !== Roles.Course_Author
  ) {
    const { role, school, schoolDistrict } = user
    const qString: any = {}
    let gua = null

    if (role === Roles.Teacher) {
      gua = `?district=${schoolDistrict}&school=${school}&teacher=${user.id}`
      qString.gua = user.id
    } else if (
      role === Roles.District_Admin ||
      role === Roles.ACT_Admin ||
      role === Roles.ACT_Multi_District_Admin
    ) {
      gua = `?district=${schoolDistrict}`
      qString.gua = schoolDistrict
    } else if (role === Roles.School_Admin) {
      gua = `?district=${schoolDistrict}&school=${school}`
      qString.gua = school
    }

    const paths = {
      // @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
      "District Admin": `/manage/organization/schools${gua}`,
      // @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
      "School Admin": `/manage/organization/teachers${gua}`,
      "Sales Rep": `/reports/usage-report`,
      // @deprecated DEPRECATED_MANAGE_ORG_NAV — old `/manage/organization` nav usage; remove or repoint to `/reporting/organization` when that nav is deleted.
      Teacher: `/manage/organization/classes${gua}`
    }
    if (licenses && licenses?.length) {
      const sortedLicenses = licenses
        .filter(license => !license?.isExpired)
        .map(item => ({ ...item }))
        .sort((a, b) => {
          const nameA = a.name.toLowerCase(),
            nameB = b.name.toLowerCase()

          // first sort on the basis to prioritize non-bubblesheet
          const isNameABubblesheet = nameA.includes("bubble sheet")
          const isNameBBubblesheet = nameB.includes("bubble sheet")

          if (isNameABubblesheet || isNameBBubblesheet) {
            if (isNameABubblesheet && !isNameBBubblesheet) {
              return 1
            } else if (isNameBBubblesheet && !isNameABubblesheet) {
              return -1
            } else {
              return 0
            }
          }

          if (nameA < nameB)
            //sort string ascending
            return -1
          if (nameA > nameB) {
            return 1
          }
          return 0
        })

      // disabled due to performance reasons
      // if (role != Roles.Student) {
      //   //Check for Dashboard license
      //   const dashboardLicense = sortedLicenses.find(
      //     lic => lic.navCategory === "Data Dashboard" && !lic.isExpired
      //   )
      //   if (
      //     dashboardLicense &&
      //     user.licenses &&
      //     Object.keys(user.licenses).length &&
      //     user.licenses[dashboardLicense && dashboardLicense.id]
      //   ) {
      //     // Redirect to data dashboard
      //     return `/data/dashboard?${queryString.stringify(qString)}`
      //   }
      // }

      const truScoreTestLicenses = sortedLicenses.filter(
        lic => lic.navCategory === "TruScore Testing"
      )

      // This also includes all tests: Practice Test, Full Test, Diagnostic Tests
      if (truScoreTestLicenses.length) {
        const currentProgram =
          program ??
          sessionStorage?.getItem(CURRENT_PROGRAM_KEY)?.toLowerCase() ??
          ""

        let truScoreTestLicenseProgram = null

        const truScoreTestLicense =
          truScoreTestLicenses.find(license =>
            license?.name?.toLowerCase()?.split(" ")?.includes(currentProgram)
          ) ?? truScoreTestLicenses[0]

        //Redirect to TruScore test
        const assessmentSetId = truScoreTestLicense.licenseSet
        let module = ""

        if (truScoreTestLicense.name) {
          if (
            truScoreTestLicense.name.includes("Practice Test") ||
            truScoreTestLicense.name.includes("Full Test") // Fix for DSAT tests
          ) {
            module = "practice-tests"
          } else if (truScoreTestLicense.name.includes("Bubble Sheet")) {
            module = "bubblesheets"
          }

          // Below are licenses for legacy test-taking in order of which one should come first (comes last in if statements)
          if (truScoreTestLicense.name.includes("TSIA2")) {
            // currentProgram = "tsia2"
            truScoreTestLicenseProgram = "tsia2"
          }
          if (truScoreTestLicense.name.includes("STAAR")) {
            // currentProgram = "staar"
            truScoreTestLicenseProgram = "staar"
          }
          // SAT has to come first to not accidently match DSAT or PSAT
          if (truScoreTestLicense.name.includes("SAT")) {
            // currentProgram = "sat"
            truScoreTestLicenseProgram = "sat"
          }
          if (
            truScoreTestLicense.name.includes("DSAT") ||
            truScoreTestLicense.name.includes("Digital SAT")
          ) {
            // currentProgram = "dsat"
            truScoreTestLicenseProgram = "dsat"
          }
          if (truScoreTestLicense.name.includes("PSAT")) {
            // currentProgram = "psat"
            truScoreTestLicenseProgram = "psat"
          }
          if (truScoreTestLicense.name.includes("ACT")) {
            // currentProgram = "act"
            truScoreTestLicenseProgram = "act"
          }
        }

        // Study Plan diagnostic tests won't get module and program detected.
        if (module && truScoreTestLicenseProgram) {
          qString.a = assessmentSetId
          qString.y = getSchoolYear().year
          homePath = `/truscore/${module}/${truScoreTestLicenseProgram}/overview/${assessmentSetId}?${queryString.stringify(
            qString
          )}`
        }

        // This is only to handle overriding legacy STAAR license
        // if they have legacy program and Study Plan, we default to Study Plan
        const studyPlanLicenses = sortedLicenses.filter(
          license => license.navCategory === "Study Plan"
        )

        if (!studyPlanLicenses.length) {
          return homePath
        }

        const studyPlanProgram =
          currentProgram ||
          truScoreTestLicenseProgram ||
          getProgramByLicenseName(studyPlanLicenses[0].name)

        const studyPlanLicense = studyPlanLicenses.find(license =>
          license.name.split(" ").includes(studyPlanProgram?.toUpperCase())
        )

        // If they have truscore testing and Study Plan, we default to Study Plan
        // If they have truscore testing and no Study Plan, we default to truscore
        return studyPlanLicense
          ? `/eoc/${studyPlanProgram}/dashboard`
          : homePath
      } else {
        const resourcesLicense = sortedLicenses.find(
          l =>
            l.navCategory.includes("ACT Resources") ||
            l.navCategory.includes("Mastery Resources")
        )

        const studyHallLicenseSAT = sortedLicenses.find(
          lic =>
            lic.navCategory === "Study Hall" && lic.name === "SAT Study Hall"
        )
        const studyHallLicenseACT = sortedLicenses.find(
          lic =>
            lic.navCategory === "Study Hall" && lic.name === "ACT Study Hall"
        )

        const isSnapLicense = sortedLicenses.find(lic =>
          lic.navCategory.includes("snap")
        )

        const studyPlanLicenseProgram = sortedLicenses
          .find(license => license.navCategory === "Study Plan")
          ?.name?.split(" ")?.[0]
          .toLowerCase()

        const elementsLicense = sortedLicenses.find(lic =>
          lic.navCategory.includes("elements")
        )

        if (resourcesLicense) {
          qString.a = resourcesLicense.id
          const licenseName = resourcesLicense.navName.toLowerCase()
          let program = ""

          if (licenseName.includes("act")) {
            program = "act"
          }

          if (licenseName.includes("tsia2")) {
            program = "tsia2"
          }

          if (licenseName.includes("sat")) {
            program = "sat"
          }
          if (
            licenseName.includes("dsat") ||
            licenseName.includes("digital sat")
          ) {
            program = "dsat"
          }

          homePath = `/mastery/resources/${program}/lessons?${queryString.stringify(
            qString
          )}`
        } else if (studyHallLicenseACT) {
          homePath = `/truscore/study-hall/act`
        } else if (studyHallLicenseSAT) {
          homePath = `/truscore/study-hall/sat`
        } else if (isSnapLicense) {
          homePath =
            user?.role === Roles.Student ? `/snap/student` : `/snap/reports`
        } else if (studyPlanLicenseProgram) {
          homePath = `/eoc/${studyPlanLicenseProgram}`
        } else if (elementsLicense) {
          const program = getProgramByLicenseName(elementsLicense.name)
          homePath = `/snap/teacher/elements/${program}`
        } else if (paths[role]) {
          homePath = paths[role]
        }
      }
    } else if (paths[role]) {
      homePath = paths[role]
    }
  } else if (user?.role === Roles.Course_Author) {
    homePath = "/snap/author/mastery-lessons"
  } else if (user?.role === Roles.Super_Administrator) {
    homePath = "/truscore"
  } else if (user?.role === Roles.Sales_Rep) {
    homePath = `/reports/usage-report`
  }

  return homePath
}
